import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeEvent } from '@fenix/shared';
import { useAuthStore } from '@/features/auth/store';

/**
 * Conecta ao Socket.IO e invalida queries quando chegam eventos de chamadas/campanhas/leads.
 * Retorna o estado da conexão para o indicador no header.
 */
export function useRealtime() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);

  useEffect(() => {
    if (!token) return;
    const socket = io(import.meta.env.VITE_API_URL ?? '/', {
      auth: { token },
      transports: ['websocket'],
      reconnectionDelayMax: 10_000,
    });
    socketRef.current = socket;
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    const onCall = (ev: RealtimeEvent) => {
      setLastEvent(ev);
      void qc.invalidateQueries({ queryKey: ['calls'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      if (ev.type === 'call.updated') void qc.invalidateQueries({ queryKey: ['leads', ev.call.leadId] });
    };
    socket.on('call.updated', onCall);
    socket.on('lead.updated', (ev: RealtimeEvent) => {
      setLastEvent(ev);
      void qc.invalidateQueries({ queryKey: ['leads'] });
    });
    socket.on('campaign.updated', (ev: RealtimeEvent) => {
      setLastEvent(ev);
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, qc]);

  return { connected, lastEvent };
}
